import { AlertCircle, CheckCircle2, CreditCard, Loader2, Mail } from 'lucide-react'
import { Badge, Dialog, DialogBody, DialogContent, Tooltip } from '@bb/ui'
import { de, formatDate, formatDateTime, formatRelative } from '../../i18n'
import { dataSourceStatusLabel, dataSourceStatusTone } from './dataSourceStatus'
import { useAdminUser } from './useUsers'
import type { AccountDataSource } from './types'

export interface AccountDetailDialogProps {
  userId: string | null
  onOpenChange: (open: boolean) => void
}

/**
 * §6's detail dialog, grouped exactly as the spec lists it: Person, Firma,
 * Nutzung, Datenquellen, Verwaltetes Postfach. Nothing here is a password
 * hash, a credential, a mailbox password or a token — the API's own
 * `AccountDetail` type never carries one, so there is nothing to
 * accidentally render.
 */
export function AccountDetailDialog({ userId, onOpenChange }: AccountDetailDialogProps) {
  const detail = useAdminUser(userId)
  const person = detail.data?.person

  return (
    <Dialog open={userId !== null} onOpenChange={onOpenChange}>
      <DialogContent
        width="lg"
        title={de.users.detail.title}
        description={person ? `${person.firstName} ${person.lastName}` : undefined}
      >
        <DialogBody className="space-y-6">
          {detail.isPending || !detail.data ? (
            <div className="flex justify-center py-16"><Loader2 className="size-6 animate-spin text-ink-faint" /></div>
          ) : (
            <>
              <Section title={de.users.detail.person}>
                <Row label={de.users.columns.name} value={`${detail.data.person.firstName} ${detail.data.person.lastName}`} />
                <Row label={de.users.columns.email} value={detail.data.person.email} />
                <Row label={de.users.columns.role} value={detail.data.person.role === 'owner' ? de.users.role.owner : de.users.role.member} />
                <Row label={de.users.columns.isAdmin} value={detail.data.person.isAdmin ? de.users.yes : de.users.no} />
                <Row label={de.users.detail.registeredAt} value={formatDate(detail.data.person.createdAt)} />
              </Section>

              <Section title={de.users.detail.company}>
                <Row label={de.users.columns.company} value={detail.data.company.name} />
                <Row label={de.users.detail.vatId} value={detail.data.company.vatId ?? '—'} />
                <Row label={de.users.detail.taxNumber} value={detail.data.company.taxNumber ?? '—'} />
                <Row
                  label={de.users.detail.invoiceNumberPattern}
                  value={`${detail.data.company.invoiceNumberPattern} · ${detail.data.company.invoiceNumberNext}`}
                />
                <Row label={de.users.detail.otherMembers} value={String(detail.data.company.otherMembers)} />
              </Section>

              <Section title={de.users.detail.usage}>
                <Row label={de.dashboard.volume.transactions} value={String(detail.data.usage.transactions)} />
                <Row label={de.dashboard.volume.documents} value={String(detail.data.usage.documents)} />
                <Row label={de.dashboard.volume.invoices} value={String(detail.data.usage.invoices)} />
                <Row
                  label={de.users.detail.lastActiveAt}
                  value={detail.data.usage.lastActiveAt ? formatRelative(detail.data.usage.lastActiveAt) : de.common.never}
                />
              </Section>

              {/* §6: "das ist der Abschnitt, wegen dessen man den Dialog
                  öffnet" — the widest section, one card per source rather
                  than a cramped row of columns. */}
              <Section title={de.users.detail.dataSources}>
                {detail.data.dataSources.length === 0 ? (
                  <p className="text-base text-ink-muted">{de.users.detail.noDataSources}</p>
                ) : (
                  <div className="space-y-2">
                    {detail.data.dataSources.map((source) => <DataSourceCard key={source.id} source={source} />)}
                  </div>
                )}
              </Section>

              <Section title={de.users.detail.managedMailbox} last>
                {detail.data.managedMailbox ? (
                  <p className="text-base text-ink">{detail.data.managedMailbox.email}</p>
                ) : (
                  <p className="text-base text-ink-faint">{de.users.detail.noMailbox}</p>
                )}
              </Section>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function Section({ title, children, last }: { title: string; children: React.ReactNode; last?: boolean }) {
  return (
    <section className={last ? undefined : 'border-b border-rule pb-5'}>
      <h3 className="mb-2 text-xs font-medium tracking-wide text-ink-muted">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="shrink-0 text-xs text-ink-faint">{label}</span>
      <span className="min-w-0 truncate text-base text-ink" title={value}>{value}</span>
    </div>
  )
}

function DataSourceCard({ source }: { source: AccountDataSource }) {
  const Icon = source.type === 'stripe' ? CreditCard : Mail
  const tone = dataSourceStatusTone(source.lastRunStatus)

  return (
    <div className="rounded-[var(--radius-control)] border border-rule px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Icon aria-hidden className="size-4 shrink-0 text-ink-faint" />
        <span className="font-medium text-ink">{source.name}</span>
        {!source.enabled ? <Badge tone="neutral">{de.users.detail.disabled}</Badge> : null}
        {source.managed ? <Badge tone="accent">{de.users.detail.managed}</Badge> : null}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
        <span className="flex items-center gap-1">
          {tone === 'danger' ? <AlertCircle className="size-3.5 text-danger" /> : null}
          {tone === 'success' ? <CheckCircle2 className="size-3.5 text-success" /> : null}
          {dataSourceStatusLabel(source.lastRunStatus)}
        </span>
        <span>
          {de.users.detail.lastRun}{' '}
          {source.lastRunAt ? (
            <Tooltip content={formatDateTime(source.lastRunAt)}>
              <span>{formatRelative(source.lastRunAt)}</span>
            </Tooltip>
          ) : de.users.detail.never}
        </span>
      </div>

      {source.lastRunStatus === 'error' && source.lastRunMessage ? (
        <p className="mt-1.5 text-xs text-danger">{de.users.detail.lastError}: {source.lastRunMessage}</p>
      ) : null}
    </div>
  )
}
